import React from 'react';
import classNames from 'classnames';
import {
  Button,
  Asset,
  ArrowBackwardIcon,
  ArrowForwardIcon,
} from '@clearcover/atlas';
import { FormInput } from '../FormInput';
import styles from './styles.module.css';

const ELLIPSIS = '\u2026';

export const DEFAULT_RESULTS_PER_PAGE = 50;

export type PaginateQueryParams = {
  paginate: {
    limit: number;
    offset: number;
  };
};

export type PaginationType = {
  limit: number;
  total: number;
  offset: number;
};

export type OffsetPaginationParamsType = {
  total: number;
  limit?: number;
  offset?: number;
  onOffsetChange: (limit: number, offset: number) => void;
  onLimitChange?: (limit: number) => void;
  className?: string;
};

export type PageNumberPaginationParamsType = {
  total: number;
  limit?: number;
  pageNumber?: number;
  onPageChange: (pageNumber: number) => void;
  onLimitChange?: (limit: number) => void;
  className?: string;
};

export type PaginationParamsType = {
  total: number;
  currentPage: number;
  limit?: number;
  handleNextPage: (nextPage: string | number) => void;
  onLimitChange?: (limit: number) => void;
  className?: string;
};

const fillPaging = (totalPages: number, currentPage: number) => {
  if (totalPages <= 6) {
    return [1, 2, 3, 4, 5, 6].slice(0, totalPages);
  } else if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, ELLIPSIS, totalPages];
  } else if (currentPage + 4 > totalPages) {
    return [
      1,
      ELLIPSIS,
      totalPages - 4,
      totalPages - 3,
      totalPages - 2,
      totalPages - 1,
      totalPages,
    ];
  } else {
    return [
      1,
      ELLIPSIS,
      currentPage - 2,
      currentPage - 1,
      currentPage,
      currentPage + 1,
      currentPage + 2,
      ELLIPSIS,
      totalPages,
    ];
  }
};

const BasePaginate: React.FC<PaginationParamsType> = ({
  total,
  limit = DEFAULT_RESULTS_PER_PAGE,
  currentPage,
  handleNextPage,
  onLimitChange,
  className,
}) => {
  const totalPages = Math.ceil(total / limit);
  const elements: Array<string | number> = fillPaging(totalPages, currentPage);

  return (
    <nav
      className={classNames(styles.container, className)}
      data-testid="paginate"
      aria-label="pagination"
    >
      {onLimitChange && (
        <div className={styles.itemsPerPage} data-testid="per-page-selector">
          <FormInput
            className={styles.itemsPerPageInput}
            label="ROWS PER PAGE"
            value={`${limit}`}
            options={{ 10: '10', 25: '25', 50: '50' }}
            variant="select"
            onChange={(value: string) => onLimitChange(parseInt(value))}
            labelPosition="inline"
            sizeVariant="small"
          />
        </div>
      )}
      <div className={styles.pageNavigation} data-testid="page-selector">
        <Button
          link
          size="sm"
          className={classNames(styles.arrow, {
            [styles.active]: currentPage > 1,
          })}
          data-testid="prev-button"
          key="prevButton"
          onClick={
            currentPage > 1 ? () => handleNextPage(currentPage - 1) : undefined
          }
          disabled={currentPage <= 1}
        >
          <Asset
            className={styles.prevIcon}
            icon={ArrowBackwardIcon}
            description="prev"
            height={12}
            width={12}
          />
          Prev
        </Button>
        {elements.map((page, index) => {
          const key = page === ELLIPSIS ? `ellipsis-${index}` : `page-${page}`;
          const clickable = page !== currentPage && page !== ELLIPSIS;
          return (
            <Button
              aria-current={page === currentPage ? 'page' : undefined}
              key={key}
              data-testid={key}
              link
              size="sm"
              className={classNames(styles.pageNumber, {
                [styles.active]: page === currentPage,
                [styles.clickable]: clickable,
              })}
              onClick={clickable ? () => handleNextPage(page) : undefined}
              disabled={page === ELLIPSIS}
            >
              {page}
            </Button>
          );
        })}
        <Button
          link
          size="sm"
          className={classNames(styles.arrow, {
            [styles.active]: currentPage < totalPages,
          })}
          onClick={
            currentPage < totalPages
              ? () => handleNextPage(currentPage + 1)
              : undefined
          }
          data-testid="next-button"
          key="nextButton"
          disabled={currentPage >= totalPages}
        >
          Next
          <Asset
            className={styles.nextIcon}
            icon={ArrowForwardIcon}
            description="next"
            height={12}
            width={12}
          />
        </Button>
      </div>
    </nav>
  );
};

export const PageNumberPaginate: React.FC<PageNumberPaginationParamsType> = ({
  total,
  limit = DEFAULT_RESULTS_PER_PAGE,
  pageNumber = 0,
  onLimitChange,
  onPageChange,
  className,
}) => {
  const currentPage = pageNumber + 1;
  const handleNextPage = (nextPage: string | number) => {
    onPageChange(+nextPage);
  };

  return (
    <BasePaginate
      total={total}
      limit={limit}
      currentPage={currentPage}
      handleNextPage={handleNextPage}
      onLimitChange={onLimitChange}
      className={className}
    />
  );
};

export const Paginate: React.FC<OffsetPaginationParamsType> = ({
  total,
  limit = DEFAULT_RESULTS_PER_PAGE,
  offset = 0,
  onOffsetChange,
  onLimitChange,
  className,
}) => {
  const currentPage = Math.ceil(offset / limit) + 1;
  const handleNextPage = (nextPage: string | number) => {
    onOffsetChange(limit, limit * (+nextPage - 1));
  };

  return (
    <BasePaginate
      total={total}
      limit={limit}
      currentPage={currentPage}
      handleNextPage={handleNextPage}
      onLimitChange={onLimitChange}
      className={className}
    />
  );
};
